"use client";

import { useState, useMemo, useCallback } from "react";
import type { DialogNode } from "@/lib/dialog-data";
import { flattenDialogToExchanges, extractBranches } from "@/lib/flatten-dialog";
import { CardCarousel } from "./cards/card-carousel";
import { CheatsheetList } from "./cards/cheatsheet-list";
import { BranchPills } from "./cards/branch-pills";
import { useTTS } from "@/lib/use-tts";
import { useSettings } from "@/lib/settings-context";
import { List, Layers } from "lucide-react";

type Mode = "cards" | "list";

interface CardsTabProps {
  nodes: DialogNode[];
  selectedChildMap: Map<string, string>;
}

export function CardsTab({ nodes, selectedChildMap }: CardsTabProps) {
  const [mode, setMode] = useState<Mode>("cards");
  const [currentIndex, setCurrentIndex] = useState(0);
  const { userDetails } = useSettings();
  const { speak, stop, state: ttsState } = useTTS();

  const exchanges = useMemo(
    () => flattenDialogToExchanges(nodes, selectedChildMap),
    [nodes, selectedChildMap]
  );

  const branches = useMemo(() => extractBranches(exchanges), [exchanges]);

  const activeBranchIndex = useMemo(() => {
    if (exchanges.length === 0) return 0;
    return exchanges[currentIndex]?.branchIndex ?? 0;
  }, [exchanges, currentIndex]);

  const handleBranchSelect = useCallback(
    (branchIndex: number) => {
      const target = exchanges.findIndex((c) => c?.branchIndex === branchIndex);
      if (target >= 0) setCurrentIndex(target);
    },
    [exchanges]
  );

  const handleCardTap = useCallback(
    (globalIndex: number) => {
      setCurrentIndex(globalIndex);
      setMode("cards");
    },
    []
  );

  const handleSpeak = useCallback(
    (text: string) => {
      speak(text);
    },
    [speak]
  );

  const handleStopSpeak = useCallback(() => {
    stop();
  }, [stop]);

  if (exchanges.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <p className="text-muted-foreground text-center">
          Start the dialogue first to see your cue cards here.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col" style={{ height: "calc(100vh - 140px)" }}>
      {/* Mode switcher */}
      <div className="flex items-center justify-center gap-1 px-4 pt-3 pb-1">
        <button
          onClick={() => setMode("cards")}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            mode === "cards"
              ? "bg-primary text-primary-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground hover:bg-muted"
          }`}
        >
          <Layers className="w-4 h-4" />
          Cards
        </button>
        <button
          onClick={() => setMode("list")}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            mode === "list"
              ? "bg-primary text-primary-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground hover:bg-muted"
          }`}
        >
          <List className="w-4 h-4" />
          List
        </button>
      </div>

      {/* Branch pills */}
      <BranchPills
        branches={branches}
        activeBranchIndex={activeBranchIndex}
        onSelect={handleBranchSelect}
      />

      {/* Main content */}
      {mode === "cards" ? (
        <CardCarousel
          cards={exchanges}
          currentIndex={currentIndex}
          onIndexChange={setCurrentIndex}
          userDetails={userDetails}
          onSpeak={handleSpeak}
          onStopSpeak={handleStopSpeak}
          ttsState={ttsState}
        />
      ) : (
        <CheatsheetList
          cards={exchanges}
          branches={branches}
          userDetails={userDetails}
          onCardTap={handleCardTap}
        />
      )}
    </div>
  );
}
